/*
 * Copyright 2026 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 */

import {capitalize} from './format-helper';

const NUMERIC_KEY = /^\d+$/;

/**
 * Orders two object keys for display.
 *
 * Array indices reach us as numeric strings, so they are compared as numbers to keep 4
 * ahead of 39. Named keys are unchanged: identifiers first, timestamps last, rest alphabetical.
 * @param a the first key.
 * @param b the second key.
 * @returns {number} negative if a sorts first, positive if b sorts first, 0 if equivalent.
 */
const compareFields = (a: string, b: string): number => {
    if (NUMERIC_KEY.test(a) && NUMERIC_KEY.test(b)) {
        return Number(a) - Number(b);
    }

    // Check if key includes 'id'
    const aHasId = a.includes('Id');
    const bHasId = b.includes('Id');
    // Check if key ends with 'time'
    const aHasTime = a.includes('Time');
    const bHasTime = b.includes('Time');

    // Prioritize keys with 'id'
    if (aHasId && !bHasId) {
        return -1;
    }
    if (!aHasId && bHasId) {
        return 1;
    }

    if (aHasTime && !bHasTime) {
        return 1;
    }

    if (!aHasTime && bHasTime) {
        return -1;
    }

    // If neither condition differs, sort alphabetically
    return a.localeCompare(b);
};

/**
 * Get the fields from the object, in display order.
 * @param obj to get fields from.
 * @returns {string[]} the string list.
 */
export const getSortedFields = (obj: any): string[] => {
    if (!obj) {
        return [];
    }
    return Object.keys(obj).sort(compareFields);
};

/**
 * Labels an object key for display.
 *
 * Array indices become 1-based row numbers so a list of 42 entries reads 1 to 42
 * rather than 0 to 41. Named keys are capitalized as before.
 * @param field the key.
 * @returns {string} the label.
 */
export const formatFieldLabel = (field: string): string => {
    if (NUMERIC_KEY.test(field)) {
        return `${Number(field) + 1}`;
    }
    return capitalize(field);
};
